/** A post's detail, for either programme: /redeem/posts/:id for a desk post,
 *  /drafts/:id for a campaign draft. From 1024px it is a drawer over the page
 *  it was opened from (the list, the calendar); below, a page of its own.
 *  Both have an address, so a reload or a Telegram link lands on the post.
 *
 *  Tabs: 미리보기 (how it will read, and for a manual channel the panel to put
 *  it up from), 편집, 기록. The action bar follows the post's state. */

import { useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { CalendarClock, CalendarX2, Check, ExternalLink, FolderInput, PencilLine, RotateCcw, Send, Trash2, X } from 'lucide-react'
import { isManualPlatform, type Campaign, type Draft, type RedeemPost, type ScheduleItem } from '@/api/client'
import { usePostActions } from '@/lib/postActions'
import { AppName } from '@/lib/AppName'
import { useCampaigns, useDraft, useRedeemApps, useRedeemPost, useSchedule, useTargets } from '@/lib/queries'
import { PLATFORM_LABEL, where } from '@/lib/redeem'
import { postUrlError } from '@/lib/postUrl'
import { formatFull } from '@/lib/time'
import { cn } from '@/lib/utils'
import {
  Button,
  buttonClass,
  CodeBlock,
  ConfirmDialog,
  Dialog,
  Drawer,
  ErrorState,
  Field,
  Input,
  KeyValue,
  PageHeader,
  PlatformMark,
  PostPreview,
  Segmented,
  Select,
  SkeletonRows,
  SourceTag,
  StatusPill,
  Textarea,
  When,
  errorText,
  useIsWide,
  useToast,
  type KeyValueItem,
} from '@/ui'
import { DeskPostEditor, ManualPanel } from './DeskPostForm'
import { autoApproves, coupangMetaOf } from '@/lib/coupang'
import { AutoApproveNote, CoupangPost } from '../review/CoupangDraft'
import { DraftEditor, judgeIsStale, parseMediaIds, parseMeta } from './DraftForm'
import ScheduleList from './ScheduleList'
import { abilities, publishingFor, SlotForm, STUCK_MINUTES, usePostCommands, type DetailState } from './shared'

type Tab = 'preview' | 'edit' | 'history'

// ── routes ─────────────────────────────────────────────────────────────────

/** How the detail is drawn and how it closes. With a page behind it (opened
 *  from the list or the calendar), closing goes back to that page; opened by
 *  its address, closing goes to the schedule. */
function useDetailMode(overlay: boolean) {
  const wide = useIsWide()
  const navigate = useNavigate()
  const location = useLocation()
  const bg = (location.state as DetailState | null)?.background
  const backTo = bg ? `${bg.pathname}${bg.search}` : '/schedule'
  return {
    mode: (wide ? 'drawer' : 'page') as 'drawer' | 'page',
    /** Drawn straight from its address on a wide screen: put the list under it. */
    withList: wide && !overlay && !bg,
    backTo,
    close: () => (bg ? navigate(-1) : navigate('/schedule')),
  }
}

export function RedeemPostRoute({ overlay = false }: { overlay?: boolean }) {
  const id = Number(useParams().id)
  const m = useDetailMode(overlay)
  return (
    <>
      {m.withList && <ScheduleList />}
      <RedeemPostDetail key={id} id={id} mode={m.mode} onClose={m.close} backTo={m.backTo} />
    </>
  )
}

export function DraftRoute({ overlay = false }: { overlay?: boolean }) {
  const id = Number(useParams().id)
  const m = useDetailMode(overlay)
  return (
    <>
      {m.withList && <ScheduleList />}
      <DraftDetail key={id} id={id} mode={m.mode} onClose={m.close} backTo={m.backTo} />
    </>
  )
}

// ── the frame ──────────────────────────────────────────────────────────────

interface FrameProps {
  mode: 'drawer' | 'page'
  onClose: () => void
  backTo: string
  title: ReactNode
  meta?: ReactNode
  actions?: ReactNode
  children: ReactNode
}

/** The page a detail returns to, named for the parent link. */
function backLabel(to: string): string {
  const path = to.split('?')[0]
  if (path === '/') return '오늘'
  if (path.startsWith('/review')) return '검토'
  if (path.startsWith('/results')) return '결과'
  if (path.startsWith('/redeem')) return '리딤'
  return '일정'
}

function DetailFrame({ mode, onClose, backTo, title, meta, actions, children }: FrameProps) {
  if (mode === 'drawer') {
    return (
      <Drawer open onClose={onClose} title={title} meta={meta} footer={actions}>
        {children}
      </Drawer>
    )
  }
  return (
    <>
      <PageHeader title={title} parent={{ to: backTo, label: backLabel(backTo) }}>
        {meta && <div className="flex flex-wrap items-center gap-2 text-meta text-muted-foreground">{meta}</div>}
      </PageHeader>
      {actions && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">{actions}</div>
      )}
      <div className="space-y-4">{children}</div>
    </>
  )
}

function Tabs({ tab, onChange, canEdit }: { tab: Tab; onChange: (t: Tab) => void; canEdit: boolean }) {
  const options: { value: Tab; label: string }[] = [
    { value: 'preview', label: '미리보기' },
    ...(canEdit ? [{ value: 'edit' as Tab, label: '편집' }] : []),
    { value: 'history', label: '기록' },
  ]
  return <Segmented label="보기" value={tab} onChange={onChange} options={options} className="mb-4" />
}

function Loading({ mode, onClose, backTo, error }: Omit<FrameProps, 'title' | 'children'> & { error?: unknown }) {
  return (
    <DetailFrame mode={mode} onClose={onClose} backTo={backTo} title={error ? '글을 열지 못했다' : '불러오는 중'}>
      {error ? (
        <ErrorState
          title={(error as { status?: number }).status === 404 ? '이 글은 없다 — 지워졌을 수 있다' : '불러오지 못했다'}
          error={error}
        />
      ) : (
        <SkeletonRows rows={6} />
      )}
    </DetailFrame>
  )
}

function SlotRow({ initial, label, onSubmit, onCancel }: { initial?: string | null; label: string; onSubmit: (iso: string) => Promise<unknown>; onCancel: () => void }) {
  return (
    <div className="w-full rounded-md border border-border bg-muted/40 p-2">
      <SlotForm initial={initial} label={label} submitLabel="저장" onSubmit={onSubmit} onCancel={onCancel} />
    </div>
  )
}

function OpenLink({ url }: { url: string }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" title={url} className={buttonClass('secondary', 'sm')}>
      <ExternalLink size={14} aria-hidden />글 열기
    </a>
  )
}

function InlineError({ error, onDismiss }: { error: unknown; onDismiss: () => void }) {
  if (error == null) return null
  return (
    <div className="mb-4 flex items-start gap-2">
      <ErrorState title="동작이 실패했다" error={error} className="flex-1" />
      <Button size="sm" variant="ghost" icon={X} onClick={onDismiss} aria-label="오류 닫기" />
    </div>
  )
}

// ── a desk post ────────────────────────────────────────────────────────────

function RedeemPostDetail({ id, mode, onClose, backTo }: { id: number; mode: 'drawer' | 'page'; onClose: () => void; backTo: string }) {
  const q = useRedeemPost(id)
  const post = q.data
  const apps = useRedeemApps().data?.items ?? []
  const app = apps.find((a) => a.app.id === post?.app_id)?.app
  const feed = useSchedule(
    { source: 'redeem', appId: post?.app_id, status: post?.status },
    { enabled: !!post && post.status !== 'draft' },
  )
  const item = feed.data?.items.find((i) => i.kind === 'redeem' && i.id === id)
  const [tab, setTab] = useState<Tab>('preview')
  const [err, setErr] = useState<unknown>(null)

  if (!post) return <Loading mode={mode} onClose={onClose} backTo={backTo} error={q.error} />
  const manual = isManualPlatform(post.platform)
  const canEdit = post.status !== 'published'

  return (
    <DetailFrame
      mode={mode}
      onClose={onClose}
      backTo={backTo}
      title={post.title || '(제목 없음)'}
      meta={
        <>
          <SourceTag source="redeem" />
          <PlatformMark platform={post.platform} withName />
          <span>
            {app ? <AppName app={app} apps={apps.map((a) => a.app)} /> : `app ${post.app_id}`}
            {post.platform === 'reddit' && (
              <>
                {' · '}
                <a href={`https://www.reddit.com/r/${post.channel}/`} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  r/{post.channel}
                </a>
              </>
            )}
            {manual && post.channel && ` · ${post.channel}`}
          </span>
          <StatusPill domain="post" status={post.status} />
          {post.status === 'published' ? (
            <When iso={post.published_at} />
          ) : (
            post.scheduled_at && <When iso={post.scheduled_at} mode="slot" />
          )}
          {item?.announced_at && post.status !== 'published' && <span>· 알림 보냄</span>}
        </>
      }
      actions={<RedeemActions post={post} manual={manual} onClose={onClose} onEdit={() => setTab('edit')} editing={tab === 'edit'} onError={setErr} />}
    >
      <InlineError error={err} onDismiss={() => setErr(null)} />
      <Tabs tab={tab} onChange={setTab} canEdit={canEdit} />
      {tab === 'preview' && (
        <div className="space-y-4">
          <PostPreview
            platform={post.platform}
            manual={manual}
            title={post.title}
            body={post.body}
            channel={post.channel}
            flair={post.flair}
            mediaId={post.media_id || undefined}
            renderedBody={post.rendered_body}
            renderedReply={post.rendered_reply}
            codeMode={post.code_mode}
          />
          {post.status === 'failed' && post.error_msg && (
            <div className="space-y-1">
              <p className="text-meta font-semibold text-status-failed">실패 사유</p>
              <CodeBlock>{post.error_msg}</CodeBlock>
            </div>
          )}
          {manual && <ManualPanel post={post} />}
        </div>
      )}
      {tab === 'edit' && canEdit && (
        <DeskPostEditor post={post} app={app} onSaved={() => setTab('preview')} onCancel={() => setTab('preview')} />
      )}
      {tab === 'history' && <KeyValue stack items={redeemHistory(post, item)} />}
    </DetailFrame>
  )
}

function redeemHistory(post: RedeemPost, item?: ScheduleItem): KeyValueItem[] {
  const rows: KeyValueItem[] = [
    { label: '만든 시각', value: formatFull(post.created_at) },
    { label: '고친 시각', value: formatFull(post.updated_at) },
  ]
  if (post.scheduled_at) rows.push({ label: '예약', value: <When iso={post.scheduled_at} mode="slot" /> })
  if (item?.announced_at) rows.push({ label: '알림 보냄', value: formatFull(item.announced_at) })
  if (post.published_at) rows.push({ label: '게시', value: formatFull(post.published_at) })
  if (post.external_url)
    rows.push({
      label: '글 주소',
      value: (
        <a href={post.external_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          {post.external_url}
        </a>
      ),
      mono: true,
    })
  if (post.external_id) rows.push({ label: '외부 id', value: post.external_id, mono: true })
  if (post.claim_phrase) rows.push({ label: '댓글 문구', value: `“${post.claim_phrase}”` })
  else if (post.platform === 'reddit' || post.platform === 'bluesky')
    rows.push({ label: '댓글 문구', value: '없음 — 이 글에는 코드를 보내지 않는다' })
  if (post.share_code) rows.push({ label: 'DM 코드', value: post.share_code, mono: true })
  if (post.flair) rows.push({ label: '플레어', value: post.flair })
  if (post.error_msg) rows.push({ label: '실패 사유', value: <CodeBlock>{post.error_msg}</CodeBlock> })
  if (item?.requests)
    rows.push({
      label: '요청',
      value: `${item.requests.total}건 · 대기 ${item.requests.pending} · 완료 ${item.requests.done} · 실패 ${item.requests.failed}`,
    })
  if (post.closed_at || post.close_error)
    rows.push({
      label: '종료 댓글',
      value: post.close_error ? (
        <CodeBlock className="text-status-failed">{post.close_error}</CodeBlock>
      ) : (
        <span>
          {formatFull(post.closed_at)}
          {post.close_url && (
            <>
              {' · '}
              <a href={post.close_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                댓글 열기
              </a>
            </>
          )}
        </span>
      ),
    })
  return rows
}

function RedeemActions({
  post,
  manual,
  onClose,
  onEdit,
  editing,
  onError,
}: {
  post: RedeemPost
  manual: boolean
  onClose: () => void
  onEdit: () => void
  editing: boolean
  onError: (e: unknown) => void
}) {
  const ref = { kind: 'redeem' as const, id: post.id }
  const can = abilities({ kind: 'redeem', status: post.status, platform: post.platform, scheduled_at: post.scheduled_at ?? null })
  const commands = usePostCommands({ onError })
  const actions = usePostActions()
  const toast = useToast()
  const [slotOpen, setSlotOpen] = useState(false)
  const [ask, setAsk] = useState<'delete' | 'revert' | null>(null)
  const label = PLATFORM_LABEL[post.platform] ?? post.platform

  const deleteWarn =
    post.status === 'published'
      ? manual
        ? `이 기록만 지운다 — ${label} 글은 그대로 남고, 발급된 공개 코드 기록도 남는다.`
        : `이 기록만 지운다 — ${label} 글은 그대로 남고, 받은 요청 기록도 남는다.`
      : `"${post.title.slice(0, 40)}"을 지운다. 본문도 함께 사라진다.`

  return (
    <>
      {slotOpen && (
        <SlotRow
          initial={post.scheduled_at}
          label="예약 시각"
          onCancel={() => setSlotOpen(false)}
          onSubmit={async (iso) => {
            if (await commands.schedule(ref, iso)) setSlotOpen(false)
          }}
        />
      )}
      {can.publishNow && (
        <Button
          size="sm"
          variant="primary"
          icon={Send}
          loading={commands.isPublishing(ref)}
          onClick={() => commands.askPublish(ref, where(post))}
        >
          지금 게시
        </Button>
      )}
      {can.schedule && (
        <Button size="sm" icon={CalendarClock} onClick={() => setSlotOpen((v) => !v)}>
          {can.scheduleLabel}
        </Button>
      )}
      {can.unschedule && (
        <Button size="sm" icon={CalendarX2} onClick={() => commands.askUnschedule(ref, where(post))}>
          {post.status === 'failed' ? '초안으로' : '내리기'}
        </Button>
      )}
      {post.status === 'publishing' && (
        <Button size="sm" icon={RotateCcw} onClick={() => setAsk('revert')}>
          초안으로 되돌리기
        </Button>
      )}
      {post.external_url && <OpenLink url={post.external_url} />}
      {post.status !== 'published' && !editing && (
        <Button size="sm" variant="ghost" icon={PencilLine} onClick={onEdit}>
          편집
        </Button>
      )}
      {post.status !== 'publishing' && (
        <Button size="sm" variant="ghost" icon={Trash2} className="ml-auto text-status-failed" onClick={() => setAsk('delete')}>
          삭제
        </Button>
      )}
      {commands.dialog}
      <ConfirmDialog
        open={ask === 'delete'}
        onOpenChange={(o) => !o && setAsk(null)}
        title="이 글을 지울까?"
        description={deleteWarn}
        confirmLabel="삭제"
        confirmVariant="destructive"
        onConfirm={async () => {
          try {
            await actions.deleteRedeem.mutateAsync(post.id)
            toast.success('지웠다')
            setAsk(null)
            onClose()
          } catch (e) {
            toast.error(errorText(e))
            onError(e)
            setAsk(null)
          }
        }}
      />
      <ConfirmDialog
        open={ask === 'revert'}
        onOpenChange={(o) => !o && setAsk(null)}
        title="초안으로 되돌릴까?"
        description={`${post.platform === 'bluesky' ? 'Bluesky' : 'Reddit'}에 이미 올라간 글은 지워지지 않는다. 남아 있으면 직접 삭제해야 한다.`}
        confirmLabel="되돌리기"
        onConfirm={async () => {
          try {
            await actions.revertRedeem.mutateAsync(post)
            toast.success('초안으로 되돌렸다')
          } catch (e) {
            toast.error(errorText(e))
            onError(e)
          }
          setAsk(null)
        }}
      />
    </>
  )
}

// ── a campaign draft ───────────────────────────────────────────────────────

/** The judge's ad-likeness score, 0–10: under 4 reads as a person, under 7 as
 *  borderline, the rest as an ad (the old Drafts card's bands). */
function JudgeGauge({ score, attempts, reason, stale }: { score: number; attempts: number; reason: string; stale?: boolean }) {
  const tone = score < 4 ? 'published' : score < 7 ? 'publishing' : 'failed'
  const fill = { published: 'bg-status-published', publishing: 'bg-status-publishing', failed: 'bg-status-failed' }[tone]
  const text = { published: 'text-status-published', publishing: 'text-status-publishing', failed: 'text-status-failed' }[tone]
  return (
    <div className={cn('space-y-1.5 rounded-lg border border-border bg-card p-3', stale && 'opacity-60')}>
      <div className="flex items-baseline justify-between gap-2 text-meta">
        <span className="font-semibold">{stale ? '광고 판정 (고치기 전 글)' : '광고 판정'}</span>
        <span className={cn('font-semibold tabular-nums', text)}>
          {score.toFixed(1)} / 10{' '}
          <span className="font-normal text-muted-foreground">· {stale ? '고친 글은 다시 판정하지 않았다' : `${attempts}회 시도`}</span>
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className={cn('h-full rounded-full', fill)} style={{ width: `${Math.min(100, Math.max(2, score * 10))}%` }} />
      </div>
      {reason && <p className="text-table text-muted-foreground">{reason}</p>}
    </div>
  )
}

function DraftDetail({ id, mode, onClose, backTo }: { id: number; mode: 'drawer' | 'page'; onClose: () => void; backTo: string }) {
  const q = useDraft(id)
  const d = q.data
  const campaigns = useCampaigns().data?.items ?? []
  const targets = useTargets().data ?? []
  const feed = useSchedule(
    { source: 'campaign', campaignId: d?.CampaignID, status: d?.Status },
    { enabled: !!d && !(d.Status === 'approved' && !d.ScheduledAt) },
  )
  const item = feed.data?.items.find((i) => i.kind === 'draft' && i.id === id)
  const [tab, setTab] = useState<Tab>('preview')
  const [err, setErr] = useState<unknown>(null)

  if (!d) return <Loading mode={mode} onClose={onClose} backTo={backTo} error={q.error} />
  const meta = parseMeta(d)
  const coupang = coupangMetaOf(d)
  const media = parseMediaIds(d)
  const campaign = campaigns.find((c) => c.ID === d.CampaignID)
  const account = targets.find((t) => t.id === d.TargetID)
  const subreddit = String(meta.subreddit ?? '')
  const title = String(meta.title ?? '')
  const canEdit = d.Status !== 'publishing'

  return (
    <DetailFrame
      mode={mode}
      onClose={onClose}
      backTo={backTo}
      title={title || d.BodyText.split('\n').find((l) => l.trim())?.slice(0, 60) || `초안 #${d.ID}`}
      meta={
        <>
          <SourceTag source="campaign" />
          <PlatformMark platform={d.Platform} withName />
          <span>
            {campaign?.Name ?? d.CampaignName ?? `campaign #${d.CampaignID}`}
            {subreddit && ` · r/${subreddit}`}
            {account && ` · @${account.name}`}
          </span>
          <StatusPill domain="post" status={d.Status} scheduledAt={d.ScheduledAt} />
          {autoApproves(d, campaign) && <AutoApproveNote />}
          {d.Status === 'published' && item?.published_at ? (
            <When iso={item.published_at} />
          ) : (
            d.ScheduledAt && <When iso={d.ScheduledAt} mode="slot" />
          )}
          {item?.announced_at && d.Status !== 'published' && <span>· 알림 보냄</span>}
        </>
      }
      actions={
        <DraftActions
          draft={d}
          campaigns={campaigns}
          where={subreddit ? `r/${subreddit}` : account ? `${PLATFORM_LABEL[d.Platform as 'reddit'] ?? d.Platform} @${account.name}` : d.Platform}
          externalUrl={item?.external_url}
          onEdit={() => setTab('edit')}
          editing={tab === 'edit'}
          onError={setErr}
        />
      }
    >
      <InlineError error={err} onDismiss={() => setErr(null)} />
      <Tabs tab={tab} onChange={setTab} canEdit={canEdit} />
      {tab === 'preview' && (
        <div className="space-y-4">
          {coupang ? (
            <CoupangPost meta={coupang} body={d.BodyText} account={account?.name} className="rounded-lg border border-border bg-card p-4" />
          ) : (
            <>
              <JudgeGauge score={d.JudgeScore} attempts={d.JudgeAttempts} reason={d.JudgeReason} stale={judgeIsStale(d)} />
              <PostPreview
                platform={d.Platform}
                title={title}
                body={d.BodyText}
                channel={subreddit}
                account={account?.name}
                flair={meta.flair ? String(meta.flair) : undefined}
                mediaId={media[0]}
              />
            </>
          )}
          {media.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {media.slice(1).map((m) => (
                <img key={m} src={`/api/media/${m}/file`} alt="" loading="lazy" className="h-20 rounded border border-border object-cover" />
              ))}
            </div>
          )}
          {d.Status === 'failed' && d.RejectionReason && (
            <div className="space-y-1">
              <p className="text-meta font-semibold text-status-failed">실패 사유</p>
              <CodeBlock>{d.RejectionReason}</CodeBlock>
            </div>
          )}
          {d.Status === 'rejected' && d.RejectionReason && (
            <p className="text-table text-muted-foreground">반려 사유: {d.RejectionReason}</p>
          )}
        </div>
      )}
      {tab === 'edit' && canEdit && <DraftEditor draft={d} onSaved={() => setTab('preview')} onCancel={() => setTab('preview')} />}
      {tab === 'history' && <KeyValue stack items={draftHistory(d, item, !!coupang)} />}
    </DetailFrame>
  )
}

function draftHistory(d: Draft, item?: ScheduleItem, coupang = false): KeyValueItem[] {
  const rows: KeyValueItem[] = [{ label: '만든 시각', value: formatFull(d.CreatedAt) }]
  if (d.UpdatedAt) rows.push({ label: '고친 시각', value: formatFull(d.UpdatedAt) })
  if (!coupang) {
    rows.push({
      label: '광고 판정',
      value: `${d.JudgeScore.toFixed(1)} / 10 · ${d.JudgeAttempts}회 시도${judgeIsStale(d) ? ' · 고치기 전 글의 점수' : ''}`,
    })
    if (d.JudgeReason) rows.push({ label: '판정 사유', value: d.JudgeReason })
  }
  if (d.EditsCount) rows.push({ label: '손본 횟수', value: `${d.EditsCount}회` })
  if (d.ScheduledAt) rows.push({ label: '예약', value: <When iso={d.ScheduledAt} mode="slot" /> })
  if (item?.announced_at) rows.push({ label: '알림 보냄', value: formatFull(item.announced_at) })
  if (item?.published_at) rows.push({ label: '게시', value: formatFull(item.published_at) })
  if (item?.external_url)
    rows.push({
      label: '글 주소',
      value: (
        <a href={item.external_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          {item.external_url}
        </a>
      ),
      mono: true,
    })
  if (d.RejectionReason)
    rows.push({
      label: d.Status === 'failed' ? '실패 사유' : '반려 사유',
      value: <CodeBlock>{d.RejectionReason}</CodeBlock>,
    })
  if (item?.engagement) {
    const { t, ...rest } = item.engagement
    rows.push({
      label: '반응',
      value: (
        <span>
          {Object.entries(rest)
            .map(([k, v]) => `${k} ${v}`)
            .join(' · ')}
          {t && <span className="text-muted-foreground"> ({formatFull(String(t))} 기준)</span>}
        </span>
      ),
    })
  }
  return rows
}

function DraftActions({
  draft,
  campaigns,
  where: whereText,
  externalUrl,
  onEdit,
  editing,
  onError,
}: {
  draft: Draft
  campaigns: Campaign[]
  where: string
  externalUrl?: string
  onEdit: () => void
  editing: boolean
  onError: (e: unknown) => void
}) {
  const ref = { kind: 'draft' as const, id: draft.ID }
  const can = abilities({ kind: 'draft', status: draft.Status, platform: draft.Platform, scheduled_at: draft.ScheduledAt })
  const commands = usePostCommands({ onError })
  const actions = usePostActions()
  const toast = useToast()
  const [slotOpen, setSlotOpen] = useState(false)
  const [dialog, setDialog] = useState<'reject' | 'manual' | 'revert' | 'move' | null>(null)
  const [reason, setReason] = useState('')
  const [url, setUrl] = useState('')
  const [moveTo, setMoveTo] = useState(0)
  const review = draft.Status === 'pending_review'
  const turnedDown = draft.Status === 'judge_rejected' || draft.Status === 'rejected'
  const placeholder =
    draft.Status === 'judge_rejected' && (draft.EditsCount ?? 0) === 0 && draft.BodyText.startsWith('(judge rejected')
  // A send that has been under way this long has likely died, and only then
  // can it be taken back (the server holds the same ten minutes).
  const sendingFor = publishingFor({ status: draft.Status, updated_at: draft.UpdatedAt ?? '' })
  const stuck = (sendingFor ?? 0) > STUCK_MINUTES
  const movable = ['pending_review', 'judge_rejected', 'rejected', 'approved', 'scheduled', 'failed'].includes(draft.Status)
  const others = campaigns.filter((c) => c.ID !== draft.CampaignID)
  const problem = postUrlError(url)

  const revert = async () => {
    try {
      await actions.revertDraft.mutateAsync(draft.ID)
      toast.success('검토 대기로 되돌렸다')
      setDialog(null)
    } catch (e) {
      toast.error(errorText(e))
      onError(e)
      setDialog(null)
    }
  }
  const move = async () => {
    try {
      await actions.moveDraft.mutateAsync({ id: draft.ID, campaignId: moveTo })
      toast.success(`${others.find((c) => c.ID === moveTo)?.Name ?? '다른 캠페인'}으로 옮겼다`)
      setDialog(null)
    } catch (e) {
      toast.error(errorText(e))
    }
  }

  const approve = async () => {
    try {
      const res = await actions.approve.mutateAsync({ id: draft.ID })
      toast.success(res.scheduled_at ? `승인했다 — ${formatFull(res.scheduled_at)} 슬롯` : '승인했다')
    } catch (e) {
      toast.error(errorText(e))
      onError(e)
    }
  }

  return (
    <>
      {slotOpen && (
        <SlotRow
          initial={draft.ScheduledAt}
          label="예약 시각"
          onCancel={() => setSlotOpen(false)}
          onSubmit={async (iso) => {
            if (await commands.schedule(ref, iso)) setSlotOpen(false)
          }}
        />
      )}
      {review && (
        <>
          <Button
            size="sm"
            variant="primary"
            icon={Check}
            loading={actions.approve.isPending}
            title="캠페인 페이스에 맞춰 게시 시각이 배정된다"
            onClick={() => void approve()}
          >
            승인
          </Button>
          <Button size="sm" icon={X} onClick={() => setDialog('reject')}>
            반려
          </Button>
          <Button size="sm" onClick={() => setDialog('manual')} title="직접 올린 글이면 주소를 남기고 게시됨으로 둔다">
            수동 게시 표시
          </Button>
        </>
      )}
      {can.publishNow && (
        <Button
          size="sm"
          variant="primary"
          icon={Send}
          loading={commands.isPublishing(ref)}
          onClick={() => commands.askPublish(ref, whereText)}
        >
          지금 게시
        </Button>
      )}
      {can.schedule && (
        <Button size="sm" icon={CalendarClock} onClick={() => setSlotOpen((v) => !v)}>
          {can.scheduleLabel}
        </Button>
      )}
      {can.unschedule && (
        <Button size="sm" icon={CalendarX2} onClick={() => commands.askUnschedule(ref, whereText)}>
          내리기
        </Button>
      )}
      {turnedDown && (
        <Button
          size="sm"
          variant="primary"
          icon={RotateCcw}
          disabled={placeholder}
          title={placeholder ? '본문이 심사 탈락 안내 글 그대로다. 먼저 편집한다.' : '검토 대기로 되돌린다. 다시 승인하면 슬롯을 받는다.'}
          onClick={() => setDialog('revert')}
        >
          검토로 되돌리기
        </Button>
      )}
      {draft.Status === 'publishing' &&
        (stuck ? (
          <Button size="sm" icon={RotateCcw} onClick={() => setDialog('revert')}>
            검토로 되돌리기
          </Button>
        ) : (
          <span className="text-meta text-muted-foreground">
            게시하는 중이다. 끝나면 결과가 여기 뜬다.
            {sendingFor != null && ` ${STUCK_MINUTES}분이 지나도 끝나지 않으면 되돌릴 수 있다.`}
          </span>
        ))}
      {movable && others.length > 0 && (
        <Button
          size="sm"
          variant="ghost"
          icon={FolderInput}
          onClick={() => {
            setMoveTo(others[0].ID)
            setDialog('move')
          }}
        >
          다른 캠페인으로 옮기기
        </Button>
      )}
      {externalUrl && <OpenLink url={externalUrl} />}
      {draft.Status !== 'publishing' && !editing && (
        <Button size="sm" variant="ghost" icon={PencilLine} onClick={onEdit}>
          편집
        </Button>
      )}
      {commands.dialog}

      <Dialog
        open={dialog === 'reject'}
        onClose={() => setDialog(null)}
        title="반려"
        description="사유는 비워 둬도 된다. 적으면 초안에 남는다."
        footer={
          <>
            <Button onClick={() => setDialog(null)}>취소</Button>
            <Button
              variant="danger"
              loading={actions.reject.isPending}
              onClick={async () => {
                try {
                  await actions.reject.mutateAsync({ id: draft.ID, reason: reason.trim() })
                  toast.success('반려했다')
                  setDialog(null)
                } catch (e) {
                  toast.error(errorText(e))
                }
              }}
            >
              반려
            </Button>
          </>
        }
      >
        <Field label="사유">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus />
        </Field>
      </Dialog>

      <Dialog
        open={dialog === 'manual'}
        onClose={() => setDialog(null)}
        title="수동 게시 표시"
        description={`${draft.Platform}에 직접 올린 글의 주소. 게시됨으로 바뀌고 결과에 잡힌다.`}
        footer={
          <>
            <Button onClick={() => setDialog(null)}>취소</Button>
            <Button
              variant="primary"
              disabled={!url.trim() || !!problem}
              loading={actions.markDraftManual.isPending}
              onClick={async () => {
                try {
                  await actions.markDraftManual.mutateAsync({ id: draft.ID, url: url.trim() })
                  toast.success('게시됨으로 표시했다')
                  setDialog(null)
                } catch (e) {
                  toast.error(errorText(e))
                }
              }}
            >
              게시됨으로 표시
            </Button>
          </>
        }
      >
        <Field label="글 주소" required error={problem}>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" autoFocus />
        </Field>
      </Dialog>

      <ConfirmDialog
        open={dialog === 'revert'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="검토로 되돌릴까?"
        description={
          draft.Status === 'publishing'
            ? `${PLATFORM_LABEL[draft.Platform as 'reddit'] ?? draft.Platform}에 글이 이미 올라갔을 수 있다. 되돌리기 전에 그 플랫폼에서 먼저 확인한다. 올라가 있으면 지워지지 않으니, 되돌린 뒤 수동 게시 표시로 주소를 남긴다.`
            : '검토 대기로 되돌린다. 점수는 고치기 전 글의 것이라 다시 판정하지 않는다. 다시 승인하면 새 슬롯을 받는다.'
        }
        confirmLabel="되돌리기"
        loading={actions.revertDraft.isPending}
        onConfirm={() => revert()}
      />

      <Dialog
        open={dialog === 'move'}
        onClose={() => setDialog(null)}
        title="다른 캠페인으로 옮기기"
        description="글은 그대로 두고 소속만 바꾼다. 게시 계정은 옮긴 캠페인에 정한 계정을 따르고, 예약된 시각은 그대로다."
        footer={
          <>
            <Button onClick={() => setDialog(null)}>취소</Button>
            <Button variant="primary" disabled={!moveTo} loading={actions.moveDraft.isPending} onClick={() => void move()}>
              옮기기
            </Button>
          </>
        }
      >
        <Field label="옮길 캠페인" required>
          <Select value={String(moveTo)} onChange={(e) => setMoveTo(Number(e.target.value))}>
            {others.map((c) => (
              <option key={c.ID} value={String(c.ID)}>
                {c.Name}
                {c.Status === 'paused' ? ' (멈춤)' : ''}
              </option>
            ))}
          </Select>
        </Field>
      </Dialog>
    </>
  )
}
